/**
 * AMASS global chrome behavior (US-004).
 *
 * Vanilla, dependency-free custom elements + helpers for:
 *   - <welcome-popup>      : first-visit 10%-off email modal, ~10s delay, localStorage suppression
 *   - <free-shipping-bar>  : live "you're $X away" message + progress fill on cart:update
 *   - <amass-header>       : sticky shadow + mobile hamburger drawer (full nav + shortcuts)
 *   - .amass-shop-cta      : persistent floating "SHOP AMASS" pill on content pages
 *   - [data-amass-swipe]   : turn product grids into horizontal swipe carousels on mobile
 *
 * The marquee (sections/announcement-bar.liquid) is pure CSS and needs no JS here.
 * Klaviyo onsite + GTM/GA4 tags are loaded by snippets/analytics.liquid (US-013).
 * This file also emits the AddToCart + InitiateCheckout dataLayer events for the
 * documented browser sequence; PageView/ViewContent are pushed in analytics.liquid.
 * The Meta pixel + CAPI + order_id Purchase dedup are owned by the native Meta
 * sales channel + Customer Events at checkout — NOT this theme (no hardcoded fbq).
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------------------- */
  /* Welcome popup                                                           */
  /* ----------------------------------------------------------------------- */
  class WelcomePopup extends HTMLElement {
    #onKeydown = null;

    connectedCallback() {
      this.storageKey = this.getAttribute('data-storage-key') || 'amass_welcome_popup_dismissed';
      this.delay = parseInt(this.getAttribute('data-delay') || '10000', 10);

      if (this.#isSuppressed()) return;

      // Do not pop in the theme editor / design mode.
      if (window.Shopify && window.Shopify.designMode) return;

      this.overlay = this.querySelector('[data-welcome-overlay]');
      this.closeBtn = this.querySelector('[data-welcome-close]');
      this.dismissBtn = this.querySelector('[data-welcome-dismiss]');
      this.form = this.querySelector('[data-welcome-form]');

      this.timer = window.setTimeout(() => this.#open(), this.delay);

      this.overlay?.addEventListener('click', () => this.#dismiss());
      this.closeBtn?.addEventListener('click', () => this.#dismiss());
      this.dismissBtn?.addEventListener('click', () => this.#dismiss());
      this.form?.addEventListener('submit', (e) => this.#onSubmit(e));
      this.#onKeydown = (e) => {
        if (e.key === 'Escape' && !this.hidden) this.#dismiss();
      };
      document.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
      if (this.timer) window.clearTimeout(this.timer);
      if (this.#onKeydown) document.removeEventListener('keydown', this.#onKeydown);
    }

    #isSuppressed() {
      try {
        return window.localStorage.getItem(this.storageKey) === '1';
      } catch (e) {
        return false;
      }
    }

    #suppress() {
      try {
        window.localStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage unavailable — popup will simply reappear next load */
      }
    }

    #open() {
      this.hidden = false;
      const focusTarget = this.querySelector('input, button, [tabindex]');
      focusTarget?.focus();
    }

    #dismiss() {
      this.hidden = true;
      this.#suppress();
      if (this.timer) window.clearTimeout(this.timer);
    }

    #onSubmit(e) {
      e.preventDefault();
      var email = (this.form.querySelector('input[name="email"]')?.value || '').trim();
      var status = this.querySelector('[data-welcome-status]');
      var say = function (msg) { if (status) { status.textContent = msg; status.hidden = false; } };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { say('Please enter a valid email address.'); return; }

      var company = this.getAttribute('data-klaviyo-company');
      var list = this.getAttribute('data-klaviyo-list');
      var self = this;
      var finish = function () {
        self.#suppress();
        if (self.form) self.form.hidden = true;
        say("You're in! Keep an eye on your inbox.");
        window.setTimeout(function () { self.hidden = true; }, 2600);
      };

      if (company && list) {
        // Klaviyo client subscriptions API — public-key, client-side by design.
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: email } } },
                custom_source: 'Welcome popup'
              },
              relationships: { list: { data: { type: 'list', id: list } } }
            }
          })
        }).then(finish, finish);
      } else {
        finish();
      }
    }
  }

  if (!customElements.get('welcome-popup')) {
    customElements.define('welcome-popup', WelcomePopup);
  }

  /* ----------------------------------------------------------------------- */
  /* Free-shipping bar — live updates on cart:update                         */
  /* ----------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = Math.round(cents / 100).toString();
    const fmt = format || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, valueNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, valueNoDecimals);
  }

  class FreeShippingBar extends HTMLElement {
    #onCartUpdate = null;

    connectedCallback() {
      this.threshold = parseInt(this.getAttribute('data-threshold') || '0', 10);
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.messageEl = this.querySelector('[data-fsb-message]');
      this.remainingEl = this.querySelector('[data-fsb-remaining]');
      this.fillEl = this.querySelector('[data-fsb-fill]');
      this.trackEl = this.querySelector('[data-fsb-track]');

      this.#onCartUpdate = (e) => this.#update(e);
      document.addEventListener('cart:update', this.#onCartUpdate);
    }

    disconnectedCallback() {
      if (this.#onCartUpdate) document.removeEventListener('cart:update', this.#onCartUpdate);
    }

    #update(event) {
      const cart = event?.detail?.resource;
      if (cart && typeof cart.total_price === 'number') {
        this.#render(cart);
      } else {
        fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        })
          .then((r) => r.json())
          .then((c) => this.#render(c))
          .catch(() => {});
      }
    }

    #hasSubscription(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;
      return cart.items.some((item) => item.selling_plan_allocation != null);
    }

    #render(cart) {
      const total = cart.total_price || 0;
      const hasSub = this.#hasSubscription(cart);
      const unlocked = hasSub || total >= this.threshold;
      const remaining = Math.max(this.threshold - total, 0);
      let progress = this.threshold > 0 ? Math.min((total / this.threshold) * 100, 100) : 100;
      if (unlocked) progress = 100;

      if (this.fillEl) this.fillEl.style.width = `${progress}%`;
      if (this.trackEl) this.trackEl.setAttribute('aria-valuenow', String(Math.round(progress)));

      this.classList.toggle('amass-fsb--unlocked', unlocked);

      if (!this.messageEl) return;
      if (unlocked) {
        this.messageEl.textContent = hasSub ? 'Your subscription ships free' : "You've earned free shipping";
      } else {
        const money = formatMoney(remaining, this.moneyFormat);
        this.messageEl.innerHTML = `You're <span data-fsb-remaining>${money}</span> away from free shipping`;
        this.remainingEl = this.messageEl.querySelector('[data-fsb-remaining]');
      }
    }
  }

  if (!customElements.get('free-shipping-bar')) {
    customElements.define('free-shipping-bar', FreeShippingBar);
  }

  /* ----------------------------------------------------------------------- */
  /* AMASS header — sticky shadow + mobile hamburger drawer                  */
  /* ----------------------------------------------------------------------- */
  class AmassHeader extends HTMLElement {
    #onScroll = null;
    #onKeydown = null;

    connectedCallback() {
      this.toggle = this.querySelector('[data-amass-menu-toggle]');
      this.menu = this.querySelector('[data-amass-mobile-menu]');
      this.overlay = this.querySelector('[data-amass-menu-overlay]');
      this.closeBtn = this.querySelector('[data-amass-menu-close]');

      this.toggle?.addEventListener('click', () => this.#openMenu());
      this.overlay?.addEventListener('click', () => this.#closeMenu());
      this.closeBtn?.addEventListener('click', () => this.#closeMenu());

      // Close after navigating via a menu link.
      this.menu?.querySelectorAll('a').forEach((a) => {
        a.addEventListener('click', () => this.#closeMenu());
      });

      this.#onKeydown = (e) => {
        if (e.key === 'Escape' && this.menu && !this.menu.hidden) this.#closeMenu();
      };
      document.addEventListener('keydown', this.#onKeydown);

      // Sticky shadow once the page is scrolled.
      if (this.getAttribute('data-sticky') === 'true') {
        this.#onScroll = () => {
          this.classList.toggle('amass-header--scrolled', window.scrollY > 8);
        };
        this.#onScroll();
        window.addEventListener('scroll', this.#onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
      if (this.#onKeydown) document.removeEventListener('keydown', this.#onKeydown);
    }

    #openMenu() {
      if (!this.menu) return;
      this.menu.hidden = false;
      // Allow the [hidden] removal to paint before transitioning in.
      requestAnimationFrame(() => this.menu.classList.add('amass-mobile-menu--open'));
      this.toggle?.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      this.menu.querySelector('a, button')?.focus();
    }

    #closeMenu() {
      if (!this.menu) return;
      this.menu.classList.remove('amass-mobile-menu--open');
      this.toggle?.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      const finish = () => {
        this.menu.hidden = true;
        this.menu.removeEventListener('transitionend', finish);
      };
      // Hide after the slide-out transition; fall back to a timeout.
      this.menu.addEventListener('transitionend', finish);
      window.setTimeout(finish, 350);
    }
  }

  if (!customElements.get('amass-header')) {
    customElements.define('amass-header', AmassHeader);
  }

  /* ----------------------------------------------------------------------- */
  /* Floating "SHOP AMASS" CTA                                               */
  /* ----------------------------------------------------------------------- */
  function initShopCta() {
    const cta = document.querySelector('.amass-shop-cta');
    if (!cta) return;

    // Hide while the footer is on screen so the CTA never covers the legal links.
    let footerVisible = false;
    const reveal = () => {
      const scrolled = window.scrollY > 200;
      const shortPage = document.body.scrollHeight <= window.innerHeight * 1.5;
      cta.classList.toggle('amass-shop-cta--visible', (scrolled || shortPage) && !footerVisible);
    };
    const footer = document.querySelector('.amass-footer, footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        footerVisible = entries.some((e) => e.isIntersecting);
        reveal();
      }).observe(footer);
    }
    reveal();
    window.addEventListener('scroll', reveal, { passive: true });
    window.addEventListener('resize', reveal, { passive: true });
  }

  /* ----------------------------------------------------------------------- */
  /* Mobile swipe carousels for product grids                               */
  /* ----------------------------------------------------------------------- */
  function initSwipeCarousels() {
    const apply = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      document.querySelectorAll('[data-amass-swipe]').forEach((grid) => {
        grid.classList.toggle('amass-swipe--active', isMobile);
      });
    };
    apply();
    window.addEventListener('resize', apply, { passive: true });
  }

  /* ----------------------------------------------------------------------- */
  /* Ecommerce analytics — AddToCart + InitiateCheckout dataLayer (US-013)   */
  /*                                                                         */
  /* Theme-side only. PageView + ViewContent are pushed by analytics.liquid. */
  /* Purchase + browser<->server CAPI dedup on order_id happen at the        */
  /* Shopify checkout via the native Meta channel + Customer Events, not here.*/
  /* ----------------------------------------------------------------------- */
  function track(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    const p = payload || {};
    p.event = eventName;
    window.dataLayer.push(p);
  }

  function initEcommerceTracking() {
    // AddToCart — Horizon dispatches CartAddEvent as a 'cart:update' on document,
    // distinguished from a generic quantity/update by a populated data.variantId.
    document.addEventListener('cart:update', (e) => {
      const data = e?.detail?.data || {};
      const isAdd = data.variantId != null && data.source !== 'cart-items-component';
      if (!isAdd) return;
      const cart = e?.detail?.resource;
      track('AddToCart', {
        currency: (cart && cart.currency) || undefined,
        content_ids: [String(data.variantId)],
        content_type: 'product',
        num_items: data.itemCount,
      });
    });

    // InitiateCheckout — fires when the shopper commits to checkout from the
    // cart page or drawer (native [name="checkout"] submit / checkout links).
    document.addEventListener('click', (e) => {
      const el = e.target && e.target.closest
        ? e.target.closest('[name="checkout"], .cart__checkout-button, a[href*="/checkout"]')
        : null;
      if (!el) return;
      fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((r) => r.json())
        .then((cart) => {
          track('InitiateCheckout', {
            currency: cart.currency,
            value: (cart.total_price || 0) / 100,
            content_ids: (cart.items || []).map((i) => String(i.variant_id)),
            num_items: cart.item_count,
          });
        })
        .catch(() => {
          // Never block the checkout navigation on a tracking failure.
          track('InitiateCheckout', {});
        });
    }, true);
  }

  /* ----------------------------------------------------------------------- */
  /* Boot                                                                     */
  /* ----------------------------------------------------------------------- */
  function boot() {
    initShopCta();
    initSwipeCarousels();
    initEcommerceTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
